/**
 * The browser's install prompt on Android (Chrome, Samsung Internet). The
 * browser fires `beforeinstallprompt` once per page load, often before the
 * log renders, so main.tsx listens from the start and the event waits here
 * for the Home Screen step's Install button. Holding it back also keeps
 * Chrome's own install banner away, so the step is the one place that asks.
 */
import { useSyncExternalStore } from "react";

/** Chromium's event; not in the DOM types, because no standard defines it. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let saved: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/** Call once, before React renders. */
export function listenForInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    saved = event as BeforeInstallPromptEvent;
    notify();
  });
  // Installed from the step or from the browser's own menu.
  window.addEventListener("appinstalled", () => {
    saved = null;
    installed = true;
    notify();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether a saved prompt can be shown, and whether Babia was just installed. */
export function useInstallPrompt(): { canPrompt: boolean; installed: boolean } {
  const canPrompt = useSyncExternalStore(subscribe, () => saved !== null);
  const done = useSyncExternalStore(subscribe, () => installed);
  return { canPrompt, installed: done };
}

/**
 * Show the browser's install dialog, inside the tap that asked for it. A
 * prompt is good for one use, so it is gone after this, whatever the answer.
 */
export async function promptInstall(): Promise<"accepted" | "dismissed" | null> {
  const event = saved;
  if (!event) return null;
  // Cleared at once, so a second tap can't prompt again; the step learns
  // after the answer, so the button doesn't change under the dialog.
  saved = null;
  try {
    await event.prompt();
    return (await event.userChoice).outcome;
  } finally {
    notify();
  }
}
